import React from 'react';
import Button from '../common/Button';

export default function AdminTourList({
    t,
    i18n,
    filteredTours,
    agencies,
    propertyTypes,
    cities,
    handleEditTourClick,
    handleDeleteTour,
    tourIdFilter,
    setTourIdFilter,
    agencyNameFilter,
    setAgencyNameFilter
}) {
    return (
        <div className="space-y-6">
            {/* --- Tour Filters --- */}
            <div className="bg-bg-card border border-gold/20 shadow rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-text-muted mb-1">{t('filterByPropertyId')}</label>
                        <input
                            type="text"
                            className="bg-bg-soft border border-gold/20 text-text rounded px-3 py-2 w-full focus:ring-gold focus:border-gold placeholder-textMuted/50"
                            placeholder={t('propertyId')}
                            value={tourIdFilter}
                            onChange={e => setTourIdFilter(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-muted mb-1">{t('filterByAgencyName')}</label>
                        <input
                            type="text"
                            className="bg-bg-soft border border-gold/20 text-text rounded px-3 py-2 w-full focus:ring-gold focus:border-gold placeholder-textMuted/50"
                            placeholder={t('agencyName')}
                            value={agencyNameFilter}
                            onChange={e => setAgencyNameFilter(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* --- Tours List --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(filteredTours || []).map(tour => (
                    <div key={tour.id} className="bg-bg-card border border-gold/20 shadow-lg rounded-lg overflow-hidden flex flex-col h-full hover:shadow-gold/10 transition-all duration-300">
                        <div className="relative group">
                            <a href={`/view-tour/${tour.id}?lang=${i18n.language}`} target="_blank" rel="noopener noreferrer" className="block relative">
                                {tour.thumbnail_path ? (
                                    <img src={`${import.meta.env.VITE_API_URL || ''}/static/${tour.thumbnail_path}`} alt={tour.title} className="w-full h-48 object-cover group-hover:opacity-90 transition-opacity" />
                                ) : (
                                    <div className="w-full h-48 bg-gray-100 flex items-center justify-center text-text-muted">No Image</div>
                                )}
                                {/* Status Badge */}
                                <div className="absolute top-2 left-2">
                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium backdrop-blur-md ${tour.status === 'draft' ? 'bg-gray-800/80 text-gray-200 border border-gray-600' :
                                        tour.status === 'active' ? 'bg-success-900/80 text-success-200 border border-success-700' :
                                            tour.status === 'reserved' ? 'bg-info-900/80 text-info-200 border border-info-700' :
                                                'bg-danger-900/80 text-danger-200 border border-danger-700'
                                        }`}>
                                        {t(`status${tour.status.charAt(0).toUpperCase() + tour.status.slice(1)}`)}
                                    </span>
                                </div>
                            </a>
                        </div>

                        <div className="p-4 flex flex-col flex-grow">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h4 className="text-lg font-bold text-text line-clamp-1" title={tour.title}>{tour.title}</h4>
                                    <p className="text-sm text-text-muted">{t('propertyId')}: <span className="text-gold">{tour.property_id}</span></p>

                                    <div className="flex items-center space-x-3 mt-3 mb-3 p-2 bg-gray-50 rounded-lg border border-gold/10">
                                        {agencies.find(a => a.id === tour.agency_id)?.logo ? (
                                            <img
                                                src={`${import.meta.env.VITE_API_URL || ''}/static/agency_logos/${agencies.find(a => a.id === tour.agency_id)?.logo}`}
                                                alt="Agency Logo"
                                                className="h-10 w-10 object-contain"
                                            />
                                        ) : (
                                            <div className="h-10 w-10 rounded-full bg-surface border border-gold/20 flex items-center justify-center text-gold">
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                                            </div>
                                        )}
                                        <div>
                                            <p className="text-xs text-text-muted uppercase font-semibold tracking-wide">{t('agency')}</p>
                                            <p className="text-sm font-medium text-text line-clamp-1">{i18n.language === 'ar' ? (agencies.find(a => a.id === tour.agency_id)?.name_ar || agencies.find(a => a.id === tour.agency_id)?.name) : (agencies.find(a => a.id === tour.agency_id)?.name_en || agencies.find(a => a.id === tour.agency_id)?.name)}</p>
                                        </div>
                                    </div>

                                    {tour.property_type && (
                                        <p className="text-sm text-text-muted">
                                            {propertyTypes && propertyTypes.find(pt => pt.en.toLowerCase() === tour.property_type.toLowerCase())
                                                ? (i18n.language === 'ar' 
                                                    ? propertyTypes.find(pt => pt.en.toLowerCase() === tour.property_type.toLowerCase()).ar 
                                                    : (i18n.language === 'es' && propertyTypes.find(pt => pt.en.toLowerCase() === tour.property_type.toLowerCase()).es 
                                                        ? propertyTypes.find(pt => pt.en.toLowerCase() === tour.property_type.toLowerCase()).es 
                                                        : propertyTypes.find(pt => pt.en.toLowerCase() === tour.property_type.toLowerCase()).en))
                                                : tour.property_type}
                                        </p>
                                    )}
                                    {tour.city && (
                                        <p className="text-sm text-text-muted">
                                            {(() => {
                                                const cityObj = cities?.find(c => c.en.toLowerCase() === tour.city?.toLowerCase());
                                                const subareaObj = cityObj?.subareas?.find(s => s.en.toLowerCase() === tour.area?.toLowerCase());
                                                const translatedCity = i18n.language === 'ar' ? (cityObj?.ar || tour.city) : (cityObj?.en || tour.city);
                                                const translatedArea = tour.area 
                                                    ? (i18n.language === 'ar' ? (subareaObj?.ar || tour.area) : (subareaObj?.en || tour.area)) 
                                                    : '';
                                                return translatedArea 
                                                    ? (i18n.language === 'ar' ? `${translatedCity}، ${translatedArea}` : `${translatedCity}, ${translatedArea}`) 
                                                    : translatedCity;
                                            })()}
                                        </p>
                                    )}
                                    <div className="mt-2 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gold border border-gold/20">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                        </svg>
                                        {tour.view_count || 0} {t('views')}
                                    </div>
                                </div>
                                <div className="flex flex-col space-y-2 ml-4 whitespace-nowrap">
                                    <Button
                                        onClick={() => handleEditTourClick(tour)}
                                        variant="secondary"
                                        className="py-2 px-3 text-sm flex items-center gap-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                        </svg>
                                        {t('edit')}
                                    </Button>
                                    <Button
                                        onClick={() => handleDeleteTour(tour.id)}
                                        variant="secondary"
                                        className="py-2 px-3 text-sm flex items-center gap-2 text-danger-500 border-danger-500/20 hover:bg-danger-500/10 hover:border-danger-500"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        {t('delete')}
                                    </Button>
                                </div>
                            </div>
                            <div className="mt-auto pt-4 border-t border-gold/10">
                                <a
                                    href={`/view-tour/${tour.id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="bg-gold-gradient text-white border-none px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1 hover:shadow-lg hover:opacity-90 transition-all w-full justify-center"
                                >
                                    {t('viewTour')} &rarr;
                                </a>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
